import React, { ReactElement, useContext, useState } from 'react'
import { AppContext } from '../contexts/AppContext'
import { useAuth0 } from '@auth0/auth0-react'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faUserSlash, faPlaneCircleExclamation, faMagnifyingGlassChart, faSprayCanSparkles } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { AppContextState } from '../contexts/AppContext.d'
import { authenticationFailed } from '../site/Login'
library.add(faUserSlash, faPlaneCircleExclamation, faMagnifyingGlassChart, faSprayCanSparkles)

/** Number of seconds to show a short-lived notification, e.g. data version changed */
export const NOTIFICATIONS_SHOWN_FOR = 7

const NavNotificationBarWrapper = ({ spacingOnly }: { spacingOnly?: boolean }): ReactElement => {
    const { appState } = useContext(AppContext)
    const { isLoading, isAuthenticated } = useAuth0()
    if (appState.notification?.message) {
        return <NavNotificationBar { ...appState.notification } spacingOnly={ spacingOnly } />
    } else if (appState.offlineMode) {
        return <NavNotificationBar
            spacingOnly={ spacingOnly }
            message="You appear to be offline."
            className="error"
            icon="plane-circle-exclamation" />
    } else if (authenticationFailed(isLoading, isAuthenticated, appState)) {
        return <NavNotificationBar
            spacingOnly={ spacingOnly }
            className="error"
            icon="user-slash"
            message="An error occurred with your session. Check your Internet connection.
            If this problem persists on stable Internet, you may need to log out and back in." />
    }
    return <NavNotificationBar spacingOnly={ spacingOnly } />
}

type NotificationProps = AppContextState['notification'] & {
    className?: string
    /** This makes an invisible copy of the bar which is just to prevent occlusion of the main page */
    spacingOnly?: boolean
}
const NavNotificationBar = (props: NotificationProps): ReactElement => {
    /* In order to persist the message while the bar is prettily transitioning down, we cache its previous content */
    const [latestProps, setLatestProps] = useState<NotificationProps | undefined>(undefined)
    if (props.message && props.message !== latestProps?.message) {
        setLatestProps({ message: props.message, className: props.className, icon: props.icon, flipped: props.flipped })
    }
    const shownClass = props.message ? ' nav-notification-bar-shown' : ''
    const spacingClass = props.spacingOnly ? ' nav-notification-bar-spacing' : ''
    const propsClass = props.className || latestProps?.className || ''
    const currentProps: NotificationProps = {
        message: props.message || latestProps?.message,
        className: `nav-notification-bar${shownClass}${spacingClass}${propsClass ? ` ${propsClass}` : ''}`,
        icon: props.icon || latestProps?.icon,
        flipped: props.flipped || latestProps?.flipped
    }
    return <div className={ currentProps.className }>
        <div className="nav-notification-bar-clip">
            <div className="nav-notification-bar-content">
                { currentProps.icon && <FontAwesomeIcon icon={ currentProps.icon } flip={ currentProps.flipped && 'horizontal' } /> }
                { currentProps.message }
            </div>
        </div>
    </div>
}

export default NavNotificationBarWrapper
